import { Select, Stack } from '@mantine/core';
import { VMultiSelect, VSelect } from '@vision/ui/components';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import type { ReportsDetailGraphicSchemaValues } from '@vision/ui/interfaces';
import { useMatrixSelection } from './useMatrixSelection';
import { dimensionValues, emptySelection, matchingPairs } from './selection';
import { formatPeriod } from './format';
import type { BenchmarkMatrixSelection } from './types';

export function BenchmarkMatrixControls() {
  const { t, i18n } = useTranslation('page-reports');
  const form = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const selection = form.values.selectedBenchmarkMatrix || emptySelection;
  const { setup, setups, measures, catalogs } = useMatrixSelection(selection);
  const update = (changes: Partial<BenchmarkMatrixSelection>) =>
    form.setFieldValue('selectedBenchmarkMatrix', { ...selection, savedRows: undefined, ...changes });
  const rows = catalogs.currentData || [];
  const periods = [...new Set(rows.flatMap((row) => row.pairs.map((pair) => pair.period)))];
  const coverages = [
    ...new Map(
      rows.flatMap((row) =>
        row.pairs.filter((pair) => pair.period === selection.period).map((pair) => [pair.coverage, pair] as const),
      ),
    ).values(),
  ];
  const levels = [
    ...new Map(
      rows.flatMap((row) =>
        matchingPairs(row, selection).flatMap((pair) =>
          row.levels.filter((level) => pair.levelKeys.includes(level.key)).map((level) => [level.key, level] as const),
        ),
      ),
    ).values(),
  ];
  return (
    <Stack gap={20} data-testid="section-benchmark-matrix-controls">
      <VSelect
        loading={setups.isFetching}
        aria-busy={setups.isFetching}
        label={t('benchmarkMatrix.setup')}
        searchable
        clearable
        required
        data={(setups.currentData || []).map((item) => ({ value: item.id, label: item.basic.name }))}
        value={selection.setupId || null}
        disabled={setups.isFetching}
        onChange={(value) => form.setFieldValue('selectedBenchmarkMatrix', { ...emptySelection, setupId: value || '' })}
        data-testid="input-matrix-setup"
      />
      <VMultiSelect
        withSelectAll
        loading={measures.isFetching}
        aria-busy={measures.isFetching}
        label={t('benchmarkMatrix.measures')}
        searchable
        clearable
        required
        data={(measures.currentData || []).map((item) => ({ value: item.id, label: item.name }))}
        value={selection.measureIds}
        disabled={!setup || measures.isFetching}
        onChange={(measureIds) => update({ measureIds, period: '', coverage: '', levelKeys: [], pairs: {} })}
        data-testid="input-matrix-measures"
      />
      <VSelect
        loading={catalogs.isFetching}
        aria-busy={catalogs.isFetching}
        label={t('benchmarkMatrix.internalPeriod')}
        description={t('benchmarkMatrix.exclusiveEnd')}
        searchable
        required
        data={periods.map((value) => ({ value, label: formatPeriod(value, i18n.language, setup?.basic.timeZone) }))}
        value={selection.period || null}
        disabled={!periods.length || catalogs.isFetching}
        onChange={(value) => update({ period: value || '', coverage: '', levelKeys: [], pairs: {} })}
        data-testid="input-matrix-period"
      />
      <VSelect
        loading={catalogs.isFetching}
        aria-busy={catalogs.isFetching}
        label={t('benchmarkMatrix.externalCoverage')}
        searchable
        required
        data={coverages.map((pair) => ({
          value: pair.coverage,
          label: `${pair.waveName} · ${formatPeriod(pair.coverage, i18n.language, setup?.basic.timeZone, true)}`,
        }))}
        value={selection.coverage || null}
        disabled={!selection.period || catalogs.isFetching}
        onChange={(value) => update({ coverage: value || '', levelKeys: [], pairs: {} })}
        data-testid="input-matrix-coverage"
      />
      <VMultiSelect
        loading={catalogs.isFetching}
        aria-busy={catalogs.isFetching}
        withSelectAll={false}
        label={t('benchmarkMatrix.levels')}
        searchable
        required
        data={levels.map((level) => ({ value: level.key, label: level.name }))}
        value={selection.levelKeys}
        disabled={!selection.coverage || catalogs.isFetching}
        onChange={(levelKeys) => update({ levelKeys })}
        data-testid="input-matrix-levels"
      />
      <Select
        label={t('benchmarkMatrix.dimension')}
        data={[
          { value: '', label: t('benchmarkMatrix.overall') },
          ...Object.keys(dimensionValues).map((value) => ({ value, label: t(`benchmarkMatrix.dimensions.${value}`) })),
        ]}
        value={selection.dimension}
        onChange={(dimension) => update({ dimension: dimension || '', dimensionValue: '' })}
        data-testid="input-matrix-dimension"
      />
      {selection.dimension && (
        <Select
          label={t('benchmarkMatrix.dimensionValue')}
          required
          searchable
          data={dimensionValues[selection.dimension].map((value) => ({
            value,
            label: t(`benchmarkMatrix.values.${value}`, { defaultValue: value }),
          }))}
          value={selection.dimensionValue || null}
          onChange={(value) => update({ dimensionValue: value || '' })}
          data-testid="input-matrix-dimension-value"
        />
      )}
      {rows.map((row) => {
        const pairs = matchingPairs(row, selection);
        if (pairs.length < 2) return null;
        const name = measures.currentData?.find((item) => item.id === row.measureId)?.name;
        return (
          <VSelect
            key={row.measureId}
            loading={catalogs.isFetching}
            aria-busy={catalogs.isFetching}
            disabled={catalogs.isFetching}
            label={t('benchmarkMatrix.choosePair', { name })}
            description={t('benchmarkMatrix.ambiguous')}
            required
            searchable
            data={pairs.map((pair) => ({
              value: pair.id,
              label: `${pair.internalName} · ${pair.externalName} (${pair.internalId} / ${pair.externalId})`,
            }))}
            value={selection.pairs[row.measureId] || null}
            onChange={(value) => update({ pairs: { ...selection.pairs, [row.measureId]: value || '' } })}
            data-testid={`input-matrix-pair-${row.measureId}`}
          />
        );
      })}
    </Stack>
  );
}
